import { useState } from 'react';
import api from '../../services/api';
import { X, Upload, FileVideo } from 'lucide-react';
import { motion } from 'framer-motion';
import WheelPicker from '../shared/WheelPicker';

export default function UploadVideoModal({ isOpen, onClose, onSuccess, moduleId, sections = [] }) {
  const [formData, setFormData] = useState({
    title_pt: '',
    description_pt: '',
    order_index: 0,
    section_id: null
  });
  const [videoFile, setVideoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('video/')) {
        setError('Por favor, selecione um arquivo de vídeo');
        return;
      }
      setVideoFile(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!videoFile) {
      setError('Selecione um arquivo de vídeo');
      return;
    }

    setLoading(true);
    setError('');
    setUploadProgress(0);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('video', videoFile);
      formDataToSend.append('module_id', moduleId);
      formDataToSend.append('title_pt', formData.title_pt);
      formDataToSend.append('title_en', formData.title_pt);
      formDataToSend.append('description_pt', formData.description_pt || '');
      formDataToSend.append('description_en', formData.description_pt || '');
      formDataToSend.append('order_index', formData.order_index);
      if (formData.section_id) {
        formDataToSend.append('section_id', formData.section_id);
      }

      console.log('📤 Enviando vídeo...');

      const { data } = await api.post('/videos/upload', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          console.log(`📊 Upload: ${percentCompleted}%`);
        }
      });

      console.log('✅ Vídeo enviado:', data);
      onSuccess(data);
      onClose();
      
      // Reset form
      setFormData({
        title_pt: '',
        description_pt: '',
        order_index: 0
      });
      setVideoFile(null);
      setUploadProgress(0);
    } catch (err) {
      console.error('❌ Erro ao enviar vídeo:', err);
      setError(err.response?.data?.details || err.response?.data?.error || 'Erro ao fazer upload do vídeo');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={!loading ? onClose : undefined}
      />

      {/* Modal */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative bg-background-800 border border-gray-700 rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-text-primary">
            Upload de Vídeo
          </h2>
          {!loading && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Upload de arquivo */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Arquivo de Vídeo
            </label>
            <div className="relative">
              <input
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                disabled={loading}
                className="hidden"
                id="video-upload"
              />
              <label
                htmlFor="video-upload"
                className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-2xl cursor-pointer transition-all ${
                  videoFile
                    ? 'border-success bg-success/10'
                    : 'border-gray-600 hover:border-primary-500 bg-background-900'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {videoFile ? (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="text-center"
                  >
                    <FileVideo size={48} className="mx-auto text-success mb-2" />
                    <p className="text-sm font-semibold text-success">{videoFile.name}</p>
                    <p className="text-xs text-text-secondary mt-1">
                      {(videoFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </motion.div>
                ) : (
                  <div className="text-center">
                    <Upload size={48} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-text-secondary">
                      Clique para selecionar ou arraste o vídeo
                    </p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Título */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Título do Vídeo
            </label>
            <input
              type="text"
              value={formData.title_pt}
              onChange={(e) => setFormData({...formData, title_pt: e.target.value})}
              disabled={loading}
              className="w-full px-4 py-3 bg-background-900 border-2 border-gray-700 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all text-text-primary disabled:opacity-50"
              placeholder="Ex: Aula 01 - Introdução"
              required
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Descrição (opcional)
            </label>
            <textarea
              value={formData.description_pt}
              onChange={(e) => setFormData({...formData, description_pt: e.target.value})}
              disabled={loading}
              className="w-full px-4 py-3 bg-background-900 border-2 border-gray-700 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all text-text-primary disabled:opacity-50"
              rows="3"
              placeholder="Descreva o conteúdo do vídeo..."
            />
          </div>

          {/* Seção */}
          {sections.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-text-primary mb-2">
                Seção (opcional)
              </label>
              <select
                value={formData.section_id || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({...formData, section_id: value === '' ? null : value});
                }}
                disabled={loading}
                className="w-full px-4 py-3 bg-background-900 border-2 border-gray-700 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all text-text-primary cursor-pointer disabled:opacity-50"
              >
                <option value="">Sem seção</option>
                {sections.map(section => (
                  <option key={section.id} value={section.id}>
                    {section.title_pt}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Organize o vídeo em uma seção específica
              </p>
            </div>
          )}

          {/* Ordem */}
          <WheelPicker
            value={formData.order_index}
            onChange={(newValue) => setFormData({...formData, order_index: newValue})}
            min={1}
            max={50}
            label="Ordem de Exibição"
          />

          {/* Progress bar */}
          {loading && uploadProgress > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-2"
            >
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">Enviando...</span>
                <span className="text-primary-500 font-semibold">{uploadProgress}%</span>
              </div>
              <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-primary-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </motion.div>
          )}

          {/* Erro */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-500/10 border-2 border-red-500/50 text-red-400 px-4 py-3 rounded-2xl text-sm"
            >
              {error}
            </motion.div>
          )}

          {/* Botões */}
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-2xl font-semibold transition-all disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !videoFile}
              className="flex-1 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-2xl font-semibold transition-all disabled:opacity-50 shadow-lg shadow-primary-500/20"
            >
              {loading ? 'Enviando...' : 'Fazer Upload'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
